import { useCallback, useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useShowChangeCheck, useOnTick } from "./useShowChangeCheck";

// #3391 -- ride-level utility alerts (Blood / Lameness / Meeting requested)
// raised at a scribe station, shown on the admin ride page. Reads the EXISTING
// GET /ride/:rideId/utility-events record (#139/#1991): no new route.
// #3559: it used to poll every 10 s. Utility events live on the ride row, whose
// writes bump the ride's class counter in the show change signal, so it now
// re-reads when the change check says that class moved (plus the 60 s
// backstop) and fetches nothing otherwise. A cleared alert is simply absent
// from the next response, so it drops off on that read.
export default function useRideUtilityEvents(rideId, { showId, classId } = {}) {
  const scoreApi = useScoreApi();
  // Tagged with the ride they belong to so a rideId change never paints the
  // previous ride's alerts on the new ride while the first fetch is in flight.
  const [state, setState] = useState({ rideId: null, rows: [] });
  const { classTick } = useShowChangeCheck(showId, { classId });

  const refresh = useCallback(async (isCancelled) => {
    try {
      const rows = unwrap(await scoreApi.rides.listRideUtilityEvents(rideId)) || [];
      if (isCancelled?.()) return;
      // Compare-then-set so an unchanged payload doesn't re-render the page.
      setState((prev) =>
        prev.rideId === rideId && JSON.stringify(prev.rows) === JSON.stringify(rows)
          ? prev
          : { rideId, rows },
      );
    } catch {
      // Non-fatal: a supplementary strip, never core ride data. Keep the last
      // known alerts and retry on the next tick.
    }
  }, [scoreApi, rideId]);

  useEffect(() => {
    if (!rideId) return undefined;
    let cancelled = false;
    if (document.visibilityState !== "hidden") refresh(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [refresh, rideId]);

  useOnTick(classTick, () => {
    if (rideId) refresh();
  });

  // #3596 -- Dismiss an active alert. Any staff screen can do it and it ends
  // the alert everywhere (the server removes it from Ride.utilityEvents; a
  // second dismiss of an already-dismissed alert is a success there too).
  // Re-reads right away so the chip leaves this screen without waiting for the
  // next change tick. A failure rethrows so the Dismiss button can say so.
  const dismiss = useCallback(
    async (eventId) => {
      await scoreApi.rides.deleteRideUtilityEvent(rideId, eventId);
      await refresh();
    },
    [scoreApi, rideId, refresh],
  );

  return { events: state.rideId === rideId ? state.rows : [], dismiss };
}
